import { useEffect } from "react";
import type { CartItem } from "@/pages/store/hooks/useCart";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartItem[];
  onUpdateQuantity: (id: number, qty: number) => void;
  onRemove: (id: number) => void;
  subtotal: number;
  onCheckout: () => void;
}

function formatKES(n: number) {
  return `KES ${n.toLocaleString()}`;
}

export default function CartDrawer({ isOpen, onClose, cartItems, onUpdateQuantity, onRemove, subtotal, onCheckout }: Props) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const vat = Math.round(subtotal * 0.16);
  const total = subtotal + vat;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-[60] transition-opacity duration-300 ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      ></div>

      {/* Drawer */}
      <div
        className={`fixed right-0 top-0 h-full w-full max-w-[420px] bg-white z-[70] flex flex-col transition-transform duration-300 ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <i className="ri-shopping-basket-2-line text-teal-600 text-xl"></i>
            <h2 className="text-lg font-bold text-gray-900">Your Cart</h2>
            <span className="bg-teal-100 text-teal-700 text-xs font-bold px-2 py-0.5 rounded-full">{cartItems.length} item{cartItems.length !== 1 ? "s" : ""}</span>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full text-gray-500 transition-colors cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-400 gap-3">
              <i className="ri-shopping-basket-line text-5xl"></i>
              <p className="text-sm">Your cart is empty.</p>
              <button onClick={onClose} className="text-teal-600 text-sm font-semibold hover:underline cursor-pointer whitespace-nowrap">
                Continue Shopping
              </button>
            </div>
          ) : (
            cartItems.map((item) => (
              <div key={item.product.id} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                <div className="w-16 h-16 shrink-0 rounded-md overflow-hidden">
                  <img src={item.product.image} alt={item.product.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2">{item.product.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{item.product.unit}</p>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center border border-gray-200 rounded-md overflow-hidden">
                      <button
                        onClick={() => onUpdateQuantity(item.product.id, item.quantity - 1)}
                        className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 text-gray-600 text-sm transition-colors cursor-pointer"
                      >
                        <i className="ri-subtract-line"></i>
                      </button>
                      <span className="w-8 text-center text-sm font-semibold text-gray-900">{item.quantity}</span>
                      <button
                        onClick={() => onUpdateQuantity(item.product.id, item.quantity + 1)}
                        className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 text-gray-600 text-sm transition-colors cursor-pointer"
                      >
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                    <p className="text-sm font-bold text-teal-700">{formatKES(item.product.price * item.quantity)}</p>
                  </div>
                </div>
                <button
                  onClick={() => onRemove(item.product.id)}
                  className="self-start text-gray-400 hover:text-rose-500 transition-colors cursor-pointer w-6 h-6 flex items-center justify-center"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              </div>
            ))
          )}
        </div>

        {/* Summary & checkout */}
        {cartItems.length > 0 && (
          <div className="border-t border-gray-100 px-6 py-5 space-y-3">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Subtotal</span>
              <span>{formatKES(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>VAT (16%)</span>
              <span>{formatKES(vat)}</span>
            </div>
            <div className="flex justify-between text-base font-bold text-gray-900 pt-3 border-t border-gray-100">
              <span>Total</span>
              <span className="text-teal-700">{formatKES(total)}</span>
            </div>
            <button
              onClick={onCheckout}
              className="w-full bg-teal-600 hover:bg-teal-500 text-white font-bold py-3 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 mt-2"
            >
              <i className="ri-secure-payment-line text-base"></i>
              Proceed to Checkout
            </button>
            <button
              onClick={onClose}
              className="w-full text-gray-500 hover:text-teal-600 text-sm font-medium py-2 cursor-pointer whitespace-nowrap"
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
}
